// frontend/src/components/dashboard/services/userService.js
import apiClient from '../../../api/client';

import axios from 'axios';
import uploadService from './uploadService';

// Create axios instance with auth header
const api = axios.create({
  // baseURL: '/api/v1',
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001',

  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const userService = {
  /**
   * Get current user profile details
   * @returns {Promise<Object>} User profile data
   */
  getUserDetails: async () => {
    try {
      console.log('Fetching user details...');
      // Add /api/v1 prefix to match backend route structure
      const response = await apiClient.get('/api/v1/users/detail');
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      console.log('User details received:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  },

  /**
 * Update user profile
 * @param {Object} userData - Updated user data
 * @param {File|string|null} imageFile - Profile image file (optional)
 * @returns {Promise<Object>} Updated user data
 */
  updateUser: async (userData, imageFile = null) => {
  try {
    let profileData = { ...userData };
    
    // Process profile image if provided
    if (imageFile) {
      try {
        console.log('🖼️ Processing profile image...');
        const processedImage = await uploadService.uploadProfileImage(imageFile);
        profileData.image = processedImage;
        console.log('✅ Image processed successfully');
      } catch (imageError) {
        console.error('❌ Image processing error:', imageError);
        throw new Error(`Image processing failed: ${imageError.message}`);
      }
    }
    
    // Clean undefined values and format data properly
    const cleanedData = {
      username: profileData.username || null,
      first_name: profileData.firstName || profileData.first_name || null,
      last_name: profileData.lastName || profileData.last_name || null,
      email: profileData.email || null,
      image: profileData.image || null,
      quote: profileData.quote || null,
      facebook: profileData.facebook || null,
      instagram: profileData.instagram || null,
      x_twitter: profileData.twitter || profileData.x_twitter || null,
      linkedin: profileData.linkedin || null,
      about_me: profileData.about || profileData.about_me || null
    };

    // Remove null values to avoid sending unnecessary data
    Object.keys(cleanedData).forEach(key => {
      if (cleanedData[key] === null || cleanedData[key] === undefined) {
        delete cleanedData[key];
      }
    });

    console.log('🚀 Sending profile update to production:', {
      ...cleanedData,
      image: cleanedData.image ? 'IMAGE_DATA_PRESENT' : 'NO_IMAGE'
    });
    
    // **ENHANCED: Add timeout and retry logic for production**
    const response = await apiClient.put('/api/v1/users/edit', cleanedData, {
      timeout: 30000, // 30 second timeout for production
      retry: 2, // Retry up to 2 times
      retryDelay: 1000 // Wait 1 second between retries
    });

    console.log('✅ Profile update response received:', response.data);

    // **ENHANCED: Handle different response formats**
    if (response.data.success === false) {
      throw new Error(response.data.message || 'Update failed');
    }

    // Transform response data back to frontend format
    const transformedData = {
      // Frontend field names
      username: response.data.user.username,
      firstName: response.data.user.first_name,
      lastName: response.data.user.last_name,
      email: response.data.user.email,
      image: response.data.user.image,
      quote: response.data.user.quote,
      facebook: response.data.user.facebook,
      instagram: response.data.user.instagram,
      twitter: response.data.user.x_twitter,
      linkedin: response.data.user.linkedin,
      about: response.data.user.about_me,
      // Backend field names - add these to fix the bug
      first_name: response.data.user.first_name,
      last_name: response.data.user.last_name,
      x_twitter: response.data.user.x_twitter,
      about_me: response.data.user.about_me
    };

    return {
      success: true,
      message: response.data.message,
      user: transformedData
    };
  } catch (error) {
    console.error('❌ Error updating user in production:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      code: error.code
    });
    
    // **ENHANCED: Better error handling for production issues**
    if (error.response) {
      // Server responded with error status
      const serverMessage = error.response.data?.message || 'Server error occurred';
      const errorCode = error.response.data?.errorCode || '';
      
      let userFriendlyMessage = serverMessage;
      
      // Provide user-friendly messages for common production issues
      if (error.response.status === 503) {
        userFriendlyMessage = 'Service temporarily unavailable. Please try again in a moment.';
      } else if (error.response.status === 504) {
        userFriendlyMessage = 'Request timeout. The server may be busy. Please try again.';
      } else if (errorCode === 'ECONNREFUSED') {
        userFriendlyMessage = 'Unable to connect to the database. Please try again later.';
      } else if (errorCode === 'ER_ACCESS_DENIED_ERROR') {
        userFriendlyMessage = 'Database access error. Please contact support.';
      }
      
      const serverError = new Error(userFriendlyMessage);
      serverError.response = error.response;
      serverError.originalError = error;
      throw serverError;
    } else if (error.request) {
      // Network error
      throw new Error('Network connection error. Please check your internet connection and try again.');
    } else {
      // Other error
      throw error;
    }
  }
},

/**
 * Update user account (Admin only)
 * @param {Object} userData - User data to update
 * @returns {Promise<Object>} Response data
 */
updateUserAdmin: async (userData) => {
  try {
    const formattedData = {
      account_id: userData.account_id,
      username: userData.username,
      email: userData.email,
      first_name: userData.first_name,
      last_name: userData.last_name,
      branch_id: userData.branch_id,
      role_id: userData.role_id
    };
    
    const response = await apiClient.put('/api/v1/users/update-user-admin', formattedData);
    return response.data;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
},
  
  /**
   * Change user password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Response data
   */
    changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await apiClient.post('/api/v1/users/change-password', {
      currentPassword,
      newPassword
    });
      return response.data;
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  },

  /**
   * Get all user accounts for the current vendor - FIXED
   */
  getAllAccounts: async () => {
    try {
      console.log('🔍 Fetching accounts...');
      const response = await apiClient.get('/api/v1/users/accounts'); // Use the correct endpoint
      console.log('📊 Accounts response:', response.data);
      
      // Handle both response formats
      if (response.data.users) {
        return response.data.users;
      } else if (Array.isArray(response.data)) {
        return response.data;
      } else {
        console.warn('⚠️ Unexpected response format:', response.data);
        return [];
      }
    } catch (error) {
      console.error('❌ Error fetching accounts:', error);
      console.error('❌ Response:', error.response?.data);
      throw error;
    }
  },


  /**
   * Get all branches for the current vendor - FIXED
   */
  getAllBranches: async () => {
    try {
      const response = await apiClient.get('/api/v1/users/branches');
      console.log('🏢 Branches response:', response.data);
      
      // **FIXED: Handle both response formats**
      if (response.data.branches) {
        return response.data.branches; // New format: { success: true, branches: [...] }
      } else if (Array.isArray(response.data)) {
        return response.data; // Legacy format: [...]
      } else {
        return []; // Fallback
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      throw error;
    }
  },

  /**
   * Create a new user account
   * @param {Object} userData - New user data
   * @returns {Promise<Object>} Created user data
   */
  createUser: async (userData) => {
    try {
      console.log('📤 UserService: Creating user with data:', userData);
      
      // **ENHANCED: Validate data before sending**
      const requiredFields = ['username', 'email', 'firstName', 'lastName', 'branchId', 'roleId'];
      const missingFields = requiredFields.filter(field => !userData[field]);
      
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      // **FIXED: Proper field mapping from frontend to backend**
      const formattedData = {
        username: userData.username,
        email: userData.email,
        first_name: userData.firstName,        // ← FIXED: Map firstName to first_name
        last_name: userData.lastName,          // ← FIXED: Map lastName to last_name
        branch_id: parseInt(userData.branchId), // ← FIXED: Map branchId to branch_id
        role_id: parseInt(userData.roleId),     // ← FIXED: Map roleId to role_id
        password: userData.password || undefined // Include if provided
      };
      
      console.log('📤 UserService: Sending formatted data:', formattedData);
      
      const response = await apiClient.post('/api/v1/users/create-user', formattedData);
      
      console.log('✅ UserService: User created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ UserService: Error creating user:', error);
      console.error('Request data was:', userData);
      throw error;
    }
  },


  /**
   * Reset a user's password and send email with new password
   * @param {string|number} accountId - ID of the account
   * @param {string} email - User's email address
   * @returns {Promise<Object>} Response data
   */
  resetPassword: async (accountId, email) => {
    try {
      const response = await apiClient.post(`/api/v1/users/resend-password/${accountId}`, {
        email: email
      });
      return response.data;
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  }
};

export default userService;